import classNames from 'classnames';
import React, { ReactNode } from 'react';
import { ActionWrapper } from '../ActionWrapper';
import { Card } from '../Card';
import { CollectionSummary } from '../CollectionSummary';
import { Image } from '../Image';
import { OrganizerInfo, OrganizerInfoType } from '../OrganizerInfo';
import styles from './CreatorCollectionCard.module.scss';

export interface CreatorCollectionCardProps {
    /**
     * The URL path of the collection
     */
    slug: string;
    /**
     *  Name of the collection
     */
    name: string;
    /**
     * Summary of the collection. Will be line clamped to 4 lines on mobile and
     * 3 lines on desktop
     */
    summary?: string;
    /**
     * ID of the collection
     */
    id?: string;
    /**
     * Organizer information. If provided will render profile image
     * and name on the card
     */
    organizer?: OrganizerInfoType;
    /**
     * URL of the collection's image
     */
    imageUrl?: string;
    /**
     * Background color to render while the image is downloading. Ideally a
     * computed edge_color of the collection's image.
     */
    collectionImageBackgroundColor?: string;
    /**
     * Number of upcoming events within the collection.
     * If provided will render translated node
     */
    numUpcomingEvents?: number;
    /**
     * Callback for when a user interacts with the click targets of the card.
     * If provided and shouldOpenNewTab == false, it will programmatically
     * set window location to ensure callback is fired.
     */
    onClick?: (id: string | undefined) => void;
    /**
     * Determines if clicking on the click targets opens a new tab or navigates
     * on the current window. If true will apply noopener.
     */
    shouldOpenNewTab?: boolean;
    /**
     * Features to render into a container positioned in the right corner of the
     * image between the fold of the image and card content indicating a holistic
     * feature of the card rather than pertaining specifically to the image or
     * content respectfully.
     */
    features?: ReactNode;
    /**
     * Prop to turn the box shadow off on the component
     */
    hideBoxShadow?: boolean;
    /**
     * Class that can be passed to the wrapping card
     * by consumers as necessary.
     */
    __containerClassName?: string;
}

const CardContent: React.FunctionComponent<{ children?: ReactNode }> = (
    props,
) => {
    return (
        <div
            data-testid="creator-collection-card_content"
            className={styles.cardContent}
        >
            {props.children}
        </div>
    );
};

const Title: React.FunctionComponent<{ children?: ReactNode }> = (props) => {
    return <h2 className={styles.title}>{props.children}</h2>;
};

const Features: React.FunctionComponent<{ children?: ReactNode }> = (
    props,
) => <div className={styles.features}>{props.children}</div>;

/**
 * A card used for displaying information about a creator collection.
 *
 * Port notes vs eb-ui:
 * - eds `Card` → the local `Card` reimpl (eds-card has no bridge/Marmalade
 *   equivalent — see `../Card/Card.tsx`).
 * - React 19: `defaultProps` on a function component → a default parameter
 *   (`shouldOpenNewTab = true`).
 */
export const CreatorCollectionCard: React.FunctionComponent<
    CreatorCollectionCardProps
> = ({ shouldOpenNewTab = true, ...props }) => {
    const classes = classNames(
        styles.creatorCollectionCard,
        'creator-collection-card',
        {
            [styles.noShadow]: props.hideBoxShadow,
            [props.__containerClassName as string]:
                !!props.__containerClassName,
        },
    );

    return (
        <Card containerClassName={classes} style="neutral">
            <ActionWrapper
                path={props.slug}
                onClick={props.onClick}
                id={props.id}
                shouldOpenNewTab={shouldOpenNewTab}
            >
                <Image
                    url={props.imageUrl}
                    name={props.name}
                    backgroundColor={props.collectionImageBackgroundColor}
                    id={props.id}
                />
                <CardContent>
                    <Title>{props.name}</Title>
                    <OrganizerInfo organizer={props.organizer} />
                    <CollectionSummary
                        numUpcomingEvents={props.numUpcomingEvents}
                    >
                        {props.summary}
                    </CollectionSummary>
                    <Features>{props.features}</Features>
                </CardContent>
            </ActionWrapper>
        </Card>
    );
};
